import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app'
import { getAuth, type Auth, GoogleAuthProvider, connectAuthEmulator } from 'firebase/auth'

/**
 * Firebase configuration interface
 */
interface FirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
}

/**
 * Validates Firebase configuration environment variables
 */
function validateFirebaseConfig(): FirebaseConfig {
  const config = {
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
    storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  }

  // Validate all required environment variables
  const missingVars = Object.entries(config)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .filter(([_, value]) => !value)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .map(([key, _]) => key)

  if (missingVars.length > 0) {
    throw new Error(
      `Missing Firebase configuration environment variables: ${missingVars.join(', ')}`
    )
  }

  return config as FirebaseConfig
}

// Initialize Firebase
const firebaseConfig = validateFirebaseConfig()
const app: FirebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig)

// Initialize Firebase Auth
export const auth: Auth = getAuth(app)
const emulator = process.env.NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_URL
if (emulator) {
  if (!firebaseConfig.projectId?.startsWith("demo-") || !/^http:\/\/127\.0\.0\.1:\d+$/.test(emulator))
    throw new Error("Auth emulator requires a disposable demo project and a loopback URL")
  connectAuthEmulator(auth, emulator, { disableWarnings: true })
}

// Configure Google Auth Provider
export const googleProvider = new GoogleAuthProvider()

export { app }
export default app
